import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'

import { cn } from '@/lib/utils'

/**
 * The one card surface (spec-64-u2, U-167 / DS-7). `page` is the white page card
 * (`.vonk-card`), `section` a card inside a page section (`.vonk-subcard`), and `inset` a
 * quiet bordered panel without a shadow, for use inside one of those two: never a page
 * card in a page card. `padding` overrides the variant's own padding (12/16/24 px).
 */
const surfaceVariants = cva('', {
  variants: {
    variant: {
      page: 'vonk-card',
      section: 'vonk-subcard',
      inset: 'rounded-lg border border-border bg-card',
    },
    padding: {
      default: '',
      none: 'p-0',
      sm: 'p-3',
      md: 'p-4',
      lg: 'p-6',
    },
  },
  defaultVariants: { variant: 'page', padding: 'default' },
})

type SurfaceProps = React.ComponentProps<'div'> &
  VariantProps<typeof surfaceVariants> & { asChild?: boolean }

function Surface({ className, variant, padding, asChild = false, ...props }: SurfaceProps) {
  const Comp = asChild ? Slot.Root : 'div'
  return (
    <Comp
      data-slot="surface"
      data-variant={variant ?? 'page'}
      className={cn(surfaceVariants({ variant, padding }), className)}
      {...props}
    />
  )
}

export { Surface, surfaceVariants }
